import React from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import moment from 'moment'
import * as LicenseTypes from 'const/LicenseTypes'
import { call, put, select, all } from 'redux-saga/effects'
import { getData as apiGetData, getDataAll as apiGetDataAll, action as apiAction } from 'api/Api'
import fetchUserRoles from './fetchUserRoles'
import fetchUnreadNotificationsCount from './fetchUnreadNotificationsCount'
import fetchUserBlockerCount from './fetchUserBlockerCount'
import {
  set as setEnv
} from 'store/redux/env'
import {
  add as addUser
} from 'store/redux/users'
import {
  set as setCurrentUser,
  setSettings as setCurrentUserSettings
} from 'store/redux/currentUser'
import {
  set as setConfig
} from 'store/redux/config'
import {
  set as setLicense
} from 'store/redux/license'
import {
  set as setVersion
} from 'store/redux/version'
import {
  set as setSettings
} from 'routes/Home/redux/settings'
import {
  add as addToast
} from 'store/redux/toasts'
import {
  set as setObservedTags
} from 'store/redux/observedTags'
import {
  setColors,
  addImageSets,
} from 'store/redux/branding'

function unpackUrl (url) {
  var obj = {}
  obj.url = url
  var x = url.split('/')
  obj.protocol = x[0]
  obj.host = x[2]
  obj.hostname = obj.host.split(':')[0]
  obj.port = obj.host.split(':')[1] || ''
  obj.origin = x[0] + '//' + x[2]
  obj.search = url.split('?')[1] || ''
  obj.search = obj.search !== '' ? '?' + obj.search : ''
  var absolutePath = x.splice(3)
  absolutePath.unshift('')
  obj.pathname = absolutePath.join('/').split('?')[0]
  var hash = url.split('#')[1] || ''
  obj.hash = hash ? '#' + hash : ''
  return obj
}

const getTimeDiff = (start, serverTime) =>
  start + (Date.now() - start) / 2 - // Two side ping
  serverTime

const getMyBoardId = () => {
  return new Promise((resolve, reject) => {
    apiGetData('MyBoardId').then((data) => {
      resolve(data.Value)
    }, (error) => {
      if (error.code === 402) {
        apiAction('CreateMyBoard').then((data) => {
          resolve(data.Id)
        })
      } else if (error.code === 700) { // MyBoard disabled by config
        resolve(null)
      } else {
        reject(error)
      }
    })
  })
}

export default function * initApp () {
  const state = yield select()

  const [
    MyBoardId,
    [
      { Value: version },
      ServerTime,
      SPHostUrl,
      InstanceId,
      CurrentUser,
      ObservedTags,
      KanBoConfig,
      { Settings: BoardCollectionSettings },
      LicenseData
    ]
  ] = yield all([
    getMyBoardId(),
    call(apiGetDataAll, [
      { method: 'ProductVersion' },
      { method: 'ServerTime' },
      { method: 'SPHostUrl' },
      { method: 'InstanceId' },
      { method: 'CurrentUser' },
      { method: 'ObservedTags' },
      { method: 'KanBoConfig' },
      { method: 'BoardCollectionSettings' },
      { method: 'LicenseType' }
    ]),
    fetchUnreadNotificationsCount(),
    put(fetchUserBlockerCount()),
  ])

  const batchedActions = []

  // Env
  const startTime = Date.now()
  const timeOffset = getTimeDiff(startTime, ServerTime.Value)

  batchedActions.push(setEnv({
    instanceId: InstanceId.Value,
    ...unpackUrl(SPHostUrl.Value),
    timeOffset
  }))

  // Current User
  batchedActions.push(addUser({
    id: CurrentUser.Id,
    name: CurrentUser.Name,
    email: CurrentUser.Email,
    profileKey: CurrentUser.ProfileKey,
    licenseType: CurrentUser.LicenseType,
    isActive: CurrentUser.Active,
    isLoaded: true,
    isLoading: false
  }))
  batchedActions.push(setCurrentUser({
    id: CurrentUser.Id,
    profileKey: CurrentUser.ProfileKey,
    myBoardId: MyBoardId
  }))
  batchedActions.push(setCurrentUserSettings({
    lang: CurrentUser.Settings.Lang,
    miniMenu: CurrentUser.Settings.MiniMenu,
    activitiesDetailLevel: CurrentUser.Settings.ActivitiesDetailLevel,
    notificationsDetailLevel: CurrentUser.Settings.NotificationsDetailLevel,
    notificationOccurence: CurrentUser.Settings.NotificationOccurence,
    notificationSound: CurrentUser.Settings.NotificationSound,
    tiles: CurrentUser.Settings.Tiles
  }))

  // Observed Tags
  batchedActions.push(setObservedTags({
    tags: ObservedTags.Tags.map(Tag => Tag.Name)
  }))

  // Config
  batchedActions.push(setConfig({
    allUsersRoles: KanBoConfig.AllUsersRoles,
    errorReportingUrl: KanBoConfig.ErrorReportingUrl,
    heartbeats: KanBoConfig.Heartbeats,
    helpUrl: KanBoConfig.HelpUrl,
    incomingMail: KanBoConfig.IncomingMail,
    incomingMailDomain: KanBoConfig.IncomingMailDomain,
    isOffice365: KanBoConfig.IsOffice365,
    myBoard: KanBoConfig.KanBoMyBoard,
    publicBoards: KanBoConfig.AllowPublicBoards,
    plugins: KanBoConfig.Plugins,
    userActivity: KanBoConfig.UserActivity,
    userPresence: KanBoConfig.UserPresence,
  }))

  batchedActions.push(setColors({
    colors: KanBoConfig.Branding.Colors,
  }))

  batchedActions.push(addImageSets({
    imageSets: KanBoConfig.Branding.Backgrounds.Sets.map(ImageSet => {
      const images = {}

      for (const Image of ImageSet.Images) {
        images[Image.ImageType] = Image.Url
      }

      return {
        id: ImageSet.Id,
        name: ImageSet.Name,
        images,
      }
    }),
  }))

  // BoardCollectionSettings
  batchedActions.push(setSettings({
    allowRemoveBoard: BoardCollectionSettings.AllowRemoveBoard,
    lang: BoardCollectionSettings.Lang,
    officeAppLauncher: BoardCollectionSettings.OfficeAppLauncher,
    siteType: BoardCollectionSettings.SiteType,
    boardTemplateId: BoardCollectionSettings.BoardTemplateId,
  }))

  // License
  let license
  if (LicenseData.License) {
    license = {
      type: LicenseData.Type,
      expireCurrent: LicenseData.License.ExpireCurrent,
      expireMax: LicenseData.License.ExpireMax,
      features: LicenseData.License.Features,
      name: LicenseData.License.Name,
      writersCurrent: LicenseData.License.WritersCurrent,
      writersMax: LicenseData.License.WritersMax,
      readersCurrent: LicenseData.License.ReadersCurrent,
      readersMax: LicenseData.License.ReadersMax
    }
  } else {
    license = {
      type: LicenseData.Type
    }
  }
  batchedActions.push(setLicense(license))

  // License error
  if (
    KanBoConfig.LicenseWarnings &&
    license.type === LicenseTypes.FULL
  ) {
    const licenseDaysLeft = moment(license.expireMax).diff(license.expireCurrent, 'days')
    const licenseUsersLeft = license.writersMax - license.writersCurrent
    if (
      licenseDaysLeft <= 7 ||
      licenseUsersLeft <= 1
    ) {
      batchedActions.push(addToast({
        id: 'LICENSE_ERROR',
        type: 'danger',
        close: true,
        message: [
          licenseDaysLeft <= 7 ? (
            <div>
              <FormattedDate value={license.expireMax}>
                {(date) => (
                  <FormattedMessage id='LICENSE_WARNING_REMAINING_DAYS' values={{ date }} />
                )}
              </FormattedDate>
            </div>
          ) : null,
          licenseUsersLeft <= 1 ? (
            <div>
              <FormattedMessage id={licenseUsersLeft === 1 ? 'LICENSE_WARNING_ONE_USER_LEFT' : 'LICENSE_WARNING_NO_USERS_LEFT'} />
            </div>
          ) : null
        ]
      }))
    }
  }

  // Version
  batchedActions.push(setVersion({
    version,
    changed: version !== state.kanbo.version.version
  }))

  yield put(batchedActions)

  yield fetchUserRoles({
    payload: {
      userId: CurrentUser.Id
    }
  })
}
